import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getDocIntelligenceProjectUploads,
  getDocIntelligenceUpload,
  getDocIntelligenceUploads
} from '../../services/api/implementation/impl';
import {
  DOC_INTEL_POLL_MS,
  getReviewSessionId,
  getUploadId,
  uploadNeedsPolling
} from './docIntelUtils';

export function useDocIntelUploads({ dealId, projectId } = {}) {
  const queryClient = useQueryClient();
  const [selectedUploadId, setSelectedUploadId] = useState(null);
  const scopeKey = dealId ? `deal-${dealId}` : `project-${projectId}`;

  const listQuery = useQuery({
    queryKey: ['docIntelligenceUploads', scopeKey],
    queryFn: () =>
      dealId
        ? getDocIntelligenceUploads(dealId)
        : getDocIntelligenceProjectUploads(projectId),
    enabled: Boolean(dealId || projectId),
    refetchInterval: (query) => {
      const uploads = query.state.data?.uploads ?? [];
      const needsPoll = uploads.some((u) => uploadNeedsPolling(u.status));
      return needsPoll ? DOC_INTEL_POLL_MS : false;
    }
  });

  const uploads = listQuery.data?.uploads ?? [];

  const selectedFromList = useMemo(
    () => uploads.find((u) => getUploadId(u) === selectedUploadId) ?? null,
    [uploads, selectedUploadId]
  );

  const detailQuery = useQuery({
    queryKey: ['docIntelligenceUpload', selectedUploadId],
    queryFn: () => getDocIntelligenceUpload(selectedUploadId),
    enabled: !!selectedUploadId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return uploadNeedsPolling(status) ? DOC_INTEL_POLL_MS : false;
    }
  });

  const selectedUpload = detailQuery.data ?? selectedFromList;
  const reviewSessionId = getReviewSessionId(selectedUpload);

  useEffect(() => {
    if (!uploads.length) {
      // Keep selection after upload while the list refetches (detail query still has the row).
      if (selectedUploadId && (listQuery.isFetching || detailQuery.data)) return;
      if (selectedUploadId) return;
      setSelectedUploadId(null);
      return;
    }
    const stillExists = uploads.some((u) => getUploadId(u) === selectedUploadId);
    if (!selectedUploadId || !stillExists) {
      setSelectedUploadId(getUploadId(uploads[0]));
    }
  }, [uploads, selectedUploadId, listQuery.isFetching, detailQuery.data]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['docIntelligenceUploads', scopeKey] });
    if (selectedUploadId) {
      queryClient.invalidateQueries({ queryKey: ['docIntelligenceUpload', selectedUploadId] });
    }
  };

  const seedUpload = (upload) => {
    const uploadId = getUploadId(upload);
    if (!uploadId) return;
    queryClient.setQueryData(['docIntelligenceUploads', scopeKey], (prev) => {
      const prevUploads = prev?.uploads ?? [];
      const normalized = { ...upload, uploadId, id: uploadId };
      const without = prevUploads.filter((row) => getUploadId(row) !== uploadId);
      return {
        uploads: [normalized, ...without],
        meta: prev?.meta ?? { total: without.length + 1, limit: 50, page: 1 }
      };
    });
    queryClient.setQueryData(['docIntelligenceUpload', uploadId], (prev) => prev ?? upload);
    setSelectedUploadId(uploadId);
  };

  const selectUpload = (uploadId) => {
    setSelectedUploadId(uploadId);
  };

  return {
    uploads,
    selectedUpload,
    selectedUploadId,
    reviewSessionId,
    selectUpload,
    seedUpload,
    invalidateAll,
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching || detailQuery.isFetching,
    isError: listQuery.isError,
    error: listQuery.error,
    refetchList: listQuery.refetch
  };
}
